import { Component, Input, ChangeDetectionStrategy, Injector } from '@angular/core'
import { Store } from '@ngrx/store'
import { NavController } from 'ionic-angular'
import { UIEventHub } from '../../../services/event.service'
import { ActionCreator } from '../../../services/action.service'
import { ViewModel } from '../../../view-model'
import { BaseWidget } from '../../base-widget'
import { FormGroup, FormControl } from '@angular/forms'
import { PulldownComponent } from '@ar2/common'
import { ViewChild } from '@angular/core'
import { ModalController, ModalOptions } from 'ionic-angular'
import { ConfirmDialogPage } from '@ar2/common'
/**
 * プルダウン
 *【選択候補:items:{label:string,value:string}[]】
 *【未選択文言:placeholder:string】
 *【未選択値:defaultValue:string】
 *【未選択値選択肢:includeDefaultValue:boolean = true】
 *【ラベル内容:labelContent:string】
 *【選択値:selectedValue:string】
 *【フォームグループ:formGroup:FormGroup】
 *【フォームコントロール:formControl:FormControl】
 *【メニュー:menu:boolean】
 *【表示条件:visible:boolean】
 *【非活性:disabled:boolean】
 *【シンプル:simple:boolean】
 *【確認ダイアログ:showConfirm: boolean = false】
 *【確認ダイアログ設定:confirmOption: {title?: string, message?: string, cancelButton?: string, okButton?: string, showClose?: boolean }】
 * @author nabla2.metamodel.generator
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-pulldown-field',
  templateUrl: './pulldown-field.html',
  styles: [`
    > div.float-right {
      position: absolute;
      right: 0;
    }
    
    .menu-pulldown {
      float: right;
    }
  `]
})
export class PulldownField extends BaseWidget {
  /**
   * 選択値の表示モデルパス
   */
  @Input() viewModelPath:string

  /**
   * 選択値に対する精査エラー
   */
  @Input() errorMessages:{modelPath:string, message:string}[]

  /**
   * 選択候補
   */
  @Input() items:{label:string,value:string}[]

  /**
   * 未選択文言
   */
  @Input() placeholder:string

  /**
   * 未選択値
   */
  @Input() defaultValue:string

  /**
   * 未選択値選択肢
   */
  @Input() includeDefaultValue:boolean = true

  /**
   * ラベル内容
   */
  @Input() labelContent:string

  /**
   * フォームグループ
   */
  @Input() formGroup:FormGroup

  /**
   * フォームコントロール
   */
  @Input() formControl:FormControl

  /**
   * メニュー
   */
  @Input() menu:boolean

  /**
   * 表示条件
   */
  @Input() visible:boolean

  /**
   * 非活性
   */
  @Input() disabled:boolean

  /**
   * シンプル
   */
  @Input() simple:boolean

  /**
   * 確認ダイアログ
   */
  @Input() showConfirm: boolean = false

  /**
   * 確認ダイアログ設定
   */
  @Input() confirmOption: {title?: string, message?: string, cancelButton?: string, okButton?: string, showClose?: boolean }

  /**
   * コンストラクタ
   * @param navCtrl 画面遷移管理サービス
   * @param store Reduxストア
   */
  constructor(
    public navCtrl: NavController,
    public store: Store<{viewModel:ViewModel}>,
    public eventHub: UIEventHub,
    public actionCreator: ActionCreator,
    public injector: Injector,
  ) {
    super(injector)
  }
  /**
   * The pulldown component
   *
   * @type {PulldownComponent}
   * @memberof PulldownField
   */
  @ViewChild('pulldown')
  public pulldown: PulldownComponent;
  
  private _selectedValue : string
  
  /**
   * 選択値
   */
  @Input()
  set selectedValue(value:string) {
    this._selectedValue = value + '';
    this.setSelectedValue();
  }
  get selectedValue():string {
    return this._selectedValue
  }
  
  public getSelectedValue(data) {
    if (!data.event) return;
    const value = data.value;
    const ev = data.event;
  
    if (this.showConfirm) {
      const modalCtrl = this.injector.get(ModalController);
      const params = {
        title: this.confirmOption.title || 'Confirm',
        message: this.confirmOption.message || 'Do you accept that changing the pulldown will discard your changes?',
        cancelButtonTitle: this.confirmOption.cancelButton || 'CANCEL',
        okButtonTitle: this.confirmOption.okButton || 'OK',
        showCloseButton: this.confirmOption.showClose || true
      };
      const myModalOptions: ModalOptions = {
        showBackdrop: false,
        enableBackdropDismiss: false,
        cssClass: 'dialog-small'
      };
      const confirmModal = modalCtrl.create(
        ConfirmDialogPage,
        params,
        myModalOptions
      );
      confirmModal.onDidDismiss(data => {
        if (data.value === 'ok') {
          this.updateViewModelAndState(ev, value);
        } else {
          this.setSelectedValue();
        }
      })
      confirmModal.present()
    } else {
      this.updateViewModelAndState(ev, value);
    }
  }
  
  private setSelectedValue() {
    if (!!this.pulldown && this.pulldown.selectValue + '' !== this._selectedValue) {
      let i = this.items.findIndex(item => item.value + '' === this._selectedValue);
      if (i === -1) {
        //when selected value is not found, set 0 and clear selected value.
        i = 0;
      } else {
        //when selected value is found, set selected value.
        this.pulldown.options = this.items;
      }
      this.pulldown.onItemClick(null, i);
    }
  }
  
  get options() {
    const options = {
      options: !!this.items ? this.items : [],
      label: this.labelContent,
      defaultValue: this.defaultValue
    }
    return options;
  }
  
  ngOnInit() {
    if (!this.formControl) {
      this.formControl = new FormControl('');
    }
  }
  
  /**
   * View Model & Stateを更新する
   * 
   * @param {Event} e 
   * @param {*} value 
   * @memberof PulldownField
   */
  updateViewModelAndState(e: Event, value: any) {
    this.store.dispatch(this.actionCreator.updateViewModel(
      this.viewModelPath,
      (m) => value ? value + '' : '',
      {id:this.id, type:e.type, source:new Event(e.type)},
      `update by ${this.id}`,
    ))
    const exist = this.items.filter(e => e.value === value)
    const content = !!exist && exist.length > 0 ? exist[0]['label'] : ''
    const type = (typeof e === 'string') ? e : e.type
    this.eventHub.next({
      id: this.id,
      type: type,
      source: {},
      content: content,
    })
    if (this.selectedValue !== value) this.eventHub.next({
      id: this.id,
      type: 'change',
      source: {},
      content: content,
    })
  }
  /**
   * このコンポーネントの状態をバインドされた表示項目の状態に反映する。
   * @param event イベント
   */
  update(event:Event) {
    const node = <HTMLFormElement>event.target
    const value = node.value
    this.store.dispatch(this.actionCreator.updateViewModel(
      this.viewModelPath,
      (m) => value,
      {id:this.id, type:event.type, source:event},
      `update by ${this.id}`,
    ))
    this.on(event)
  }
}


// WEBPACK FOOTER //
// ./src/widgets/pulldown/pulldown-field/pulldown-field.component.ts